<template>
    <div class="reservation-tables-list">
        <div v-if="!isEditing" class="tables-list">
            <div v-for="table in tableAssignments" :key="table.id" class="table-tag">
                <span class="table-name">{{ table.name }}</span>
                <div v-if="!isReadonly" class="table-actions" :class="{ 'is-processing': isProcessing }">
                    <i class="fas fa-trash unassign-table" @click.stop="unassignTable(table)"></i>
                    <i class="fas fa-pen edit-table" @click.stop="editTableAssignment"></i>
                </div>
            </div>
        </div>
        <div
            v-if="!isReadonly && !isEditing && tableAssignments.length === 0"
            class="add-table-button"
            @click.stop="editTableAssignment"
        >
            <i class="fas fa-plus"></i>
        </div>
        <ReservationAssignedTablesEditor
            v-if="isEditing"
            :orderId="orderId"
            :orderItemId="reservationDetails.orderItemId"
            :dateFrom="reservationDetails.timeFrom"
            :dateTo="reservationDetails.timeTo"
            :tableAssignments="tableAssignments"
            class="tables-editor"
            @cancelEditing="onCancelEditing"
            @reservationDetailsChanged="onReservationDetailsChanged"
        />
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import type { IReservationDetails } from "@/data/model/Order";
    import type { TableAssignment } from "@/operator/data/Reservations";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, ref } from "vue";

    import ReservationAssignedTablesEditor from "./ReservationAssignedTablesEditor.vue";

    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);

    const emit = defineEmits(["reservationDetailsChanged"]);

    const props = defineProps<{
        orderId: string;
        isReadonly: boolean
        reservationDetails: IReservationDetails;
    }>();

    const isEditing = ref(false);
    const isProcessing = ref(false);
    const tableAssignments = computed<TableAssignment[]>(() =>
        props.reservationDetails.tableAssignments.map((x) => ({
            id: x.isTableCombination ? x.tableCombination!.combinationId : x.table!.tableId,
            name: x.isTableCombination ? x.tableCombination!.tableNames : x.table!.tableName,
            isCombination: x.isTableCombination,
        }))
    );

    function onCancelEditing() {
        isEditing.value = false;
    }

    function onReservationDetailsChanged(newReservationDetails: IReservationDetails) {
        isEditing.value = false;

        emit("reservationDetailsChanged", newReservationDetails);
    }

    function editTableAssignment() {
        if (isProcessing.value) return;

        isEditing.value = true;
    }

    function unassignTable(tableAssignment: TableAssignment) {
        if (isProcessing.value) return;

        Popup.show({
            message: translations.general.reallyRemove,
            confirmHandler: () => removeAssignment(tableAssignment),
            confirmButton: translations.general.remove,
            cancelButton: translations.general.cancel,
        }, $('body'));
    }

    async function removeAssignment(tableAssignment: TableAssignment) {
        try {
            isProcessing.value = true;
            const newTableAssignments = tableAssignments.value.filter((x) => x.id !== tableAssignment.id);
            const result = await operatorDao.updateAssignedTables({
                orderId: props.orderId,
                orderItemId: props.reservationDetails.orderItemId,
                tableAssignments: newTableAssignments,
            });
            emit("reservationDetailsChanged", result);
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: translations.general.error,
                message: translations.general.operationFailed,
            });
        } finally {
            isProcessing.value = false;
        }
    }

    defineExpose({
        isEditing,
    });
</script>

<style scoped lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";

    .reservation-tables-list {
        .tables-list {
            display: flex;
            flex-wrap: wrap;
            gap: 0.25em;

            .table-tag {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 0em 0.5em;
                background-color: #c9c9c9;
                color: colors.$fontColor;
                font-size: 14px;
                border-radius: 0.5em;
                // gap: 0.5em;
                min-width: 80px;
                min-height: 32px;

                .table-name {
                    flex: 1;
                    white-space: nowrap;
                    font-size: 13px;
                    margin-right: 0.25em;
                    padding-left: 0.25em;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .table-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.5em;
                    opacity: 1;
                    transition: opacity 0.2s ease;

                    i {
                        padding: 0.5em;
                        &.unassign-table {
                            padding-right: 0.25em;
                        }

                        &.edit-table {
                            padding-left: 0.25em;
                        }
                    }
                }

                &.is-processing {
                    opacity: 0.5;
                    cursor: default;
                }
            }
        }

        .add-table-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            background-color: transparent;
            color: colors.$fontColor;
            border: 2px solid #c9c9c9;
            border-radius: 50%;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 12px;

            &:hover {
                border-color: colors.$tastierGreen;
            }

            i {
                font-size: 10px;
            }
        }
    }
</style>
